import { Link } from 'react-router-dom';
import { Crown, ShieldCheck, Flame, Scale } from 'lucide-react';
import LazyImage from './LazyImage';
import { useCurrency } from '../contexts/CurrencyContext';
import { useBestSeller } from '../contexts/BestSellerContext';
import { useCompare } from '../contexts/CompareContext';
import { getScentFamilies, ScentChip } from '../utils/scentFamily';

export default function ProductCard({ product }) {
  const { formatPrice } = useCurrency();
  const { bestSellerId } = useBestSeller();
  const { has: isCompared, add: addCompare, remove: removeCompare } = useCompare();
  const isBestSellerToday = bestSellerId && bestSellerId === product.id;
  // Compute total stock across variants if applicable
  const totalStock = product.enable_variants && product.variants?.length
    ? product.variants.reduce((s, v) => s + (v.stock || 0), 0)
    : (product.stock || 0);
  const lowStock = totalStock > 0 && totalStock < 5;
  const outOfStock = totalStock === 0;

  // #7 hover crossfade — second image (gallery[0] if image_url is the primary, else gallery[1])
  const gallery = Array.isArray(product.gallery) ? product.gallery : [];
  const secondaryImage = gallery.find(g => g && g !== product.image_url);

  // #8 scent family chips
  const families = getScentFamilies(product, 1);

  return (
    <Link
      to={`/product/${product.id}`}
      className="group block"
      data-testid={`product-card-${product.id}`}
    >
      <div className="product-image-hover bg-[#141414] border border-white/5 aspect-[3/4] mb-3 overflow-hidden relative">
        <LazyImage
          src={product.image_url}
          alt={product.name}
          className="product-image-primary w-full h-full opacity-90 group-hover:opacity-100"
        />
        {secondaryImage && (
          <LazyImage
            src={secondaryImage}
            alt=""
            className="product-image-secondary w-full h-full"
          />
        )}
        {product.is_signature && (
          <div className="absolute top-0 left-0 bg-[#D4AF37] text-black px-2.5 py-1 flex items-center gap-1.5 z-10" data-testid={`signature-badge-${product.id}`}>
            <Crown className="w-3 h-3" />
            <span className="text-[10px] uppercase tracking-[0.15em] font-sans font-semibold">JS Original</span>
          </div>
        )}
        {/* Best Seller of the Day — gamified social proof, refreshes every 5 min from backend */}
        {isBestSellerToday && (
          <div
            className="absolute top-0 left-0 z-20 flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-[#D4AF37] via-[#E5C158] to-[#F3E5AB] text-black shadow-lg"
            style={{ marginTop: product.is_signature ? '24px' : '0' }}
            data-testid={`best-seller-ribbon-${product.id}`}
          >
            <Flame className="w-3 h-3" strokeWidth={2.5} />
            <span className="text-[10px] uppercase tracking-[0.18em] font-sans font-bold">Best Seller Today</span>
          </div>
        )}
        {/* #24 Authenticity seal — always-visible, works on light & dark product photos */}
        <div className="absolute bottom-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`authenticity-badge-${product.id}`}>
          <div className="flex items-center gap-1 px-2 py-1 bg-white text-[#0a3d22] border border-black/15 shadow-[0_2px_6px_rgba(0,0,0,0.25)] rounded-sm">
            <ShieldCheck className="w-3 h-3" strokeWidth={2.5} />
            <span className="text-[9px] uppercase tracking-[0.12em] font-sans font-bold">100% Authentic</span>
          </div>
        </div>
        {lowStock && (
          <div className="absolute top-0 right-0 bg-[#F59E0B] text-black px-2.5 py-1 z-10" data-testid={`low-stock-badge-${product.id}`}>
            <span className="text-[10px] uppercase tracking-[0.15em] font-sans font-bold">Only {totalStock} left</span>
          </div>
        )}
        {outOfStock && (
          <div className="absolute top-0 right-0 bg-red-500/90 text-white px-2.5 py-1 z-10" data-testid={`out-of-stock-badge-${product.id}`}>
            <span className="text-[10px] uppercase tracking-[0.15em] font-sans font-bold">Sold Out</span>
          </div>
        )}
        {/* Compare toggle — sits in the bottom-right corner so it doesn't
            clash with the signature/best-seller ribbons at the top-left or
            the stock urgency at the top-right. Uses stopPropagation so the
            user can toggle without navigating into the product page. */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isCompared(product.id)) removeCompare(product.id);
            else addCompare(product.id, product.name);
          }}
          title={isCompared(product.id) ? 'Remove from compare' : 'Add to compare'}
          data-testid={`compare-toggle-${product.id}`}
          className={`absolute bottom-2 right-2 z-10 flex items-center gap-1 px-2 py-1 transition-colors ${
            isCompared(product.id)
              ? 'bg-[#D4AF37] text-black'
              : 'bg-black/60 backdrop-blur text-white/70 hover:text-[#D4AF37] opacity-0 group-hover:opacity-100'
          }`}
        >
          <Scale className="w-3 h-3" />
          <span className="text-[9px] uppercase tracking-[0.12em] font-sans font-semibold">
            {isCompared(product.id) ? 'Comparing' : 'Compare'}
          </span>
        </button>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {product.is_signature ? (
            <span data-testid={`crafted-pill-${product.id}`} className="crafted-pill inline-flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-full border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/[0.08] via-[#D4AF37]/[0.04] to-transparent">
              <span className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#D4AF37] text-black">
                <Crown className="w-2 h-2" strokeWidth={2.8} />
              </span>
              <span className="text-[9px] uppercase tracking-[0.18em] text-[#D4AF37] font-sans font-semibold">Crafted by JSF</span>
            </span>
          ) : (
            <p className="text-xs uppercase tracking-[0.15em] text-white/40 font-sans">{product.gender}</p>
          )}
          {families[0] && <ScentChip family={families[0]} />}
        </div>
        <h3 className="text-lg font-light text-white group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {product.name}
        </h3>
        <p className="text-[#D4AF37] text-sm font-sans font-medium">
          {product.enable_variants && product.variants?.length > 0
            ? `From ${formatPrice(Math.min(...product.variants.map(v => v.price)))}`
            : formatPrice(product.base_price)
          }
        </p>
        {!product.enable_variants && !product.is_signature && (
          <p className="text-white/30 text-xs font-sans">EDP &middot; 30% Oil &middot; Extrait-Level Performance</p>
        )}
        {product.is_signature && product.signature_tagline && (
          <p className="text-white/40 text-xs font-sans italic">{product.signature_tagline}</p>
        )}
      </div>
    </Link>
  );
}
